import api, { handleApiResponse, handleApiError } from './api';

class ChatService {
  constructor() {
    this.pollingRooms = new Set(); // Track which rooms are being polled
    this.lastMessageIds = new Map(); // Track last message ID per room
  }

  // Get user chat rooms
  async getChatRooms() {
    try {
      const response = await api.get('/chat/rooms');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get or create ticket chat room
  async getTicketChatRoom(ticketId) {
    try {
      console.log('[ChatService] Getting chat room for ticket:', ticketId);
      const response = await api.get(`/chat/tickets/${ticketId}/room`);
      console.log('[ChatService] Chat room response:', response);
      return handleApiResponse(response);
    } catch (error) {
      console.error('[ChatService] Error getting chat room:', error);
      throw new Error(handleApiError(error));
    }
  }

  // Get chat messages with pagination - FIXED: No recursive polling
  async getMessages(chatRoomId, page = 1) {
    try {
      console.log('[ChatService] Getting messages for room:', chatRoomId, 'page:', page);
      const response = await api.get(`/chat/rooms/${chatRoomId}/messages`, {
        params: { page }
      });
      console.log('[ChatService] Messages response:', response);
      return handleApiResponse(response);
    } catch (error) {
      console.error('[ChatService] Error getting messages:', error);
      throw new Error(handleApiError(error));
    }
  }

  // FIXED: Check for new messages without causing recursion
  async checkForNewMessages(chatRoomId) {
    try {
      // Prevent duplicate polling
      if (this.pollingRooms.has(chatRoomId)) {
        return null;
      }

      this.pollingRooms.add(chatRoomId);
      
      const response = await api.get(`/chat/rooms/${chatRoomId}/messages`, {
        params: { page: 1, limit: 5 } // Only get latest 5 messages
      });
      
      this.pollingRooms.delete(chatRoomId);
      
      if (response?.data?.data?.messages) {
        const messages = response.data.data.messages;
        const lastKnownId = this.lastMessageIds.get(chatRoomId);
        
        // Find new messages since last check
        const newMessages = messages.filter(msg => 
          !lastKnownId || msg.id > lastKnownId
        );
        
        // Update last known message ID
        if (messages.length > 0) {
          const latestId = Math.max(...messages.map(m => m.id));
          this.lastMessageIds.set(chatRoomId, latestId);
        }
        
        return {
          newMessages: newMessages.reverse(), // Chronological order
          totalMessages: messages.reverse()
        };
      }
      
      return null;
    } catch (error) {
      this.pollingRooms.delete(chatRoomId);
      console.warn('[ChatService] Error checking for new messages:', error);
      return null;
    }
  }

  // Send text message only - FIXED: No duplicates
  async sendMessage(chatRoomId, data) {
    try {
      console.log('[ChatService] Sending message to room:', chatRoomId, 'data:', data);
      
      const requestData = {
        message: data.message,
        message_type: data.message_type || 'text'
      };

      const response = await api.post(`/chat/rooms/${chatRoomId}/messages`, requestData);
      console.log('[ChatService] Send message response:', response);
      
      const result = handleApiResponse(response);
      
      // Update last message ID to prevent duplicates
      if (result?.data?.message?.id) {
        const currentLastId = this.lastMessageIds.get(chatRoomId) || 0;
        this.lastMessageIds.set(chatRoomId, Math.max(currentLastId, result.data.message.id));
      }
      
      return result;
    } catch (error) {
      console.error('[ChatService] Error sending message:', error);
      throw new Error(handleApiError(error));
    }
  }

  // Mark chat as read
  async markAsRead(chatRoomId) {
    try {
      console.log('[ChatService] Marking room as read:', chatRoomId);
      const response = await api.patch(`/chat/rooms/${chatRoomId}/read`);
      return handleApiResponse(response);
    } catch (error) {
      console.error('[ChatService] Error marking as read:', error);
      // Don't throw error for marking as read - it's not critical
      return { data: null, error: error.message };
    }
  }

  // Add participant to chat (admin only)
  async addParticipant(chatRoomId, userId) {
    try {
      const response = await api.post(`/chat/rooms/${chatRoomId}/participants`, {
        user_id: userId
      });
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // UTILITY METHODS

  // Format message timestamp
  formatMessageTime(timestamp) {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    
    // Show date for older messages
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString();
    }
  }

  // Format chat room data for display
  formatChatRoom(chatRoom) {
    return {
      id: chatRoom.id,
      name: chatRoom.name,
      type: chatRoom.type,
      ticketId: chatRoom.ticket_id,
      participants: chatRoom.participants || [],
      unreadCount: chatRoom.unread_count || 0,
      latestMessage: chatRoom.latest_message,
      otherParticipant: chatRoom.other_participant,
      updatedAt: chatRoom.updated_at
    };
  }

  // Format message data for display
  formatMessage(message) {
    return {
      id: message.id,
      chatRoomId: message.chat_room_id,
      userId: message.user_id,
      message: message.message,
      messageType: message.message_type,
      isSystemMessage: message.is_system_message,
      user: message.user,
      createdAt: message.created_at,
      formattedTime: this.formatMessageTime(message.created_at)
    };
  }

  // Check if chat is available for ticket
  isChatAvailable(ticket, user) {
    if (!ticket || !user) return false;
    
    // Chat is available if:
    // 1. Ticket is assigned to an agent
    // 2. User is either the customer or the assigned agent
    if (!ticket.agent_id) return false;
    
    return ticket.creator_id === user.id || ticket.agent_id === user.id;
  }

  // Get chat status message
  getChatStatusMessage(ticket) {
    if (!ticket) return 'No ticket information';
    
    if (!ticket.agent_id) {
      return 'Chat will be available once an agent is assigned';
    }
    
    if (ticket.status === 'closed') {
      return 'Ticket is closed. Chat is read-only';
    }
    
    if (ticket.status === 'resolved') {
      return 'Ticket is resolved. You can still send messages if needed';
    }
    
    return 'Chat is active';
  }

  // Validate text message
  validateMessage(message) {
    if (!message || typeof message !== 'string') {
      throw new Error('Message is required');
    }
    
    if (message.trim().length === 0) {
      throw new Error('Message cannot be empty');
    }
    
    if (message.length > 2000) {
      throw new Error('Message is too long (max 2000 characters)');
    }
    
    return true;
  }

  // Check if user is online (simple check)
  isUserOnline(lastSeen) {
    if (!lastSeen) return false;
    
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffInMinutes = Math.floor((now - lastSeenDate) / (1000 * 60));
    
    return diffInMinutes < 5; // Consider online if active in last 5 minutes
  }

  // Get chat notification sound
  playNotificationSound() {
    try {
      // Simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  }

  // Get message preview for notifications
  getMessagePreview(message, maxLength = 50) {
    if (!message) return '';
    
    const text = message.trim();
    if (text.length <= maxLength) {
      return text;
    }
    
    return text.substring(0, maxLength) + '...';
  }

  // Check if message contains mentions
  containsMention(message, username) {
    if (!message || !username) return false;
    
    const mentionPattern = new RegExp(`@${username}\\b`, 'i');
    return mentionPattern.test(message);
  }

  // Escape HTML in messages for security
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Parse simple text formatting (optional)
  parseMessageFormatting(message) {
    if (!message) return '';
    
    // Escape HTML first
    let formatted = this.escapeHtml(message);
    
    // Simple formatting: **bold** and *italic*
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Simple URL detection
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    formatted = formatted.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>');
    
    return formatted;
  }

  // Clean up resources
  cleanup() {
    this.pollingRooms.clear();
    this.lastMessageIds.clear();
  }
}

export default new ChatService();